/*
 * Order attribution (VIS-258).
 *
 * Mirrors the visitor's favorite store onto the cart as the `_favorite_store`
 * attribute (the store metaobject handle), so it lands on the order as a note
 * attribute and a Flow copies it into the order's
 * `visionottica.favorite_store` metafield for ERP sale attribution.
 *
 * The attribute tracks the favorite for guests and signed-in customers alike:
 * the device-side favorite (store-favorites.js) is the value "at the moment of
 * the order". Clearing the favorite clears the attribute — an empty string
 * removes it from the cart. No favorite at checkout means no attribute and an
 * empty metafield, by design (no fallback to the shipping address).
 *
 * Steady state costs zero requests: the cart's current attribute value is
 * rendered into the config by order-attribution.liquid, so a write only fires
 * when the favorite and the cart actually diverge (page load after a change
 * made on another page, cart events, favorite toggles).
 */

import { ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { getFavorite } from '@theme/store-favorites';

/** @returns {{ itemCount: number, current: string } | null} */
function config() {
  const element = document.querySelector('[data-order-attribution-config]');
  if (!element) return null;
  try {
    const parsed = JSON.parse(element.textContent);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

const settings = config();

// The cart-side attribute value as last known/written; seeded server-side so a
// matching state never fires a request. Stale on cached guest pages, worst
// case one redundant idempotent write.
let lastWritten = settings?.current || '';

/** @returns {string} The handle the cart attribute should hold ('' = none). */
function desired() {
  return getFavorite()?.handle || '';
}

/**
 * Fire-and-forget: any failure is retried by the next trigger.
 * @param {{ initial?: boolean }} [options] - initial skips the write on page
 *   load when the cart is empty: writing would create a cart cookie and take
 *   the visitor out of the full-page cache on a view with nothing to
 *   attribute. The first add-to-cart fires cart:update and carries the
 *   favorite then. The hint is only trusted on page load (it can be stale on
 *   cached guest pages, but a visitor with a cart bypasses that cache, so a
 *   stale 0 should not hide a real cart): a later favorite change writes
 *   unconditionally.
 */
async function push({ initial = false } = {}) {
  if (!settings) return;
  const handle = desired();
  if (handle === lastWritten) return;
  if (initial && settings.itemCount === 0) return;

  try {
    // keepalive lets the write complete across a navigation to checkout.
    const response = await fetch(Theme.routes.cart_update_url, {
      ...fetchConfig('json', { body: JSON.stringify({ attributes: { _favorite_store: handle } }) }),
      keepalive: true,
    });
    if (response.ok) lastWritten = handle;
  } catch {
    // ignore — fire-and-forget
  }
}

let inFlight = false;
let dirty = false;

/**
 * Serializes concurrent triggers; a trigger landing mid-push re-runs once.
 * @param {{ initial?: boolean }} [options]
 */
async function sync(options) {
  if (inFlight) {
    dirty = true;
    return;
  }
  inFlight = true;
  try {
    await push(options);
  } finally {
    inFlight = false;
    if (dirty) {
      dirty = false;
      sync();
    }
  }
}

sync({ initial: true });
document.addEventListener(ThemeEvents.cartUpdate, () => sync());
window.addEventListener('store-favorite:changed', () => sync());
